
import React, { useState, useEffect } from 'react';
import { Play, TrendingUp, Users, Zap, ArrowUpRight } from 'lucide-react';
import KinosLeadModal from "./KinosLeadModal";

const MedSpaHeroSection = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [totalAudience, setTotalAudience] = useState(0);
  const [growthRate, setGrowthRate] = useState(0);
  const [engagementRate, setEngagementRate] = useState(0);
  const [conversions, setConversions] = useState(0);
  const [chartData, setChartData] = useState([]);
  const [barHeights, setBarHeights] = useState([0, 0, 0, 0, 0, 0]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Animate metrics with looping
  useEffect(() => {
    let intervals = [];
    let timeouts = [];
    let isMounted = true;
    let loopTimeout = null;

    const animate = () => {
      if (!isMounted) return;

      // Reset values
      setTotalAudience(0);
      setGrowthRate(0);
      setEngagementRate(0);
      setConversions(0);
      setChartData([]);
      setBarHeights([0, 0, 0, 0, 0, 0]);

      // Animate total audience (38,304)
      const audienceInterval = setInterval(() => {
        if (!isMounted) {
          clearInterval(audienceInterval);
          return;
        }
        setTotalAudience(prev => {
          if (prev >= 38304) {
            return 38304;
          }
          return Math.min(prev + 600, 38304);
        });
      }, 15);
      intervals.push(audienceInterval);

      // Animate growth rate (43%)
      const growthTimeout = setTimeout(() => {
        let current = 0;
        const growthInterval = setInterval(() => {
          if (!isMounted) {
            clearInterval(growthInterval);
            return;
          }
          if (current >= 43) {
            clearInterval(growthInterval);
          } else {
            current += 1.2;
            setGrowthRate(Math.min(current, 43));
          }
        }, 25);
        intervals.push(growthInterval);
      }, 400);
      timeouts.push(growthTimeout);

      // Animate engagement rate (68%) with progress bar
      const engagementTimeout = setTimeout(() => {
        let current = 0;
        const engagementInterval = setInterval(() => {
          if (!isMounted) {
            clearInterval(engagementInterval);
            return;
          }
          if (current >= 68) {
            clearInterval(engagementInterval);
          } else {
            current += 1.8;
            setEngagementRate(Math.min(current, 68));
          }
        }, 20);
        intervals.push(engagementInterval);
      }, 600);
      timeouts.push(engagementTimeout);

      // Animate conversions (234)
      const conversionsTimeout = setTimeout(() => {
        let current = 0;
        const conversionsInterval = setInterval(() => {
          if (!isMounted) {
            clearInterval(conversionsInterval);
            return;
          }
          if (current >= 234) {
            clearInterval(conversionsInterval);
          } else {
            current += 6;
            setConversions(Math.min(current, 234));
          }
        }, 15);
        intervals.push(conversionsInterval);
      }, 800);
      timeouts.push(conversionsTimeout);

      // Animate mini chart data
      const chartTimeout = setTimeout(() => {
        const dataPoints = [];
        for (let i = 0; i < 7; i++) {
          setTimeout(() => {
            if (!isMounted) return;
            dataPoints.push(Math.random() * 40 + 30);
            setChartData([...dataPoints]);
          }, i * 100);
        }
      }, 1000);
      timeouts.push(chartTimeout);

      // Animate bar chart
      const barTimeout = setTimeout(() => {
        const heights = [65, 45, 80, 55, 70, 60];
        heights.forEach((target, index) => {
          setTimeout(() => {
            if (!isMounted) return;
            let current = 0;
            const barInterval = setInterval(() => {
              if (!isMounted) {
                clearInterval(barInterval);
                return;
              }
              if (current >= target) {
                clearInterval(barInterval);
              } else {
                current += 2;
                setBarHeights(prev => {
                  const newHeights = [...prev];
                  newHeights[index] = Math.min(current, target);
                  return newHeights;
                });
              }
            }, 20);
            intervals.push(barInterval);
          }, index * 150);
        });
      }, 1200);
      timeouts.push(barTimeout);

      // Loop animation after completion
      loopTimeout = setTimeout(() => {
        if (isMounted) {
          intervals.forEach(i => clearInterval(i));
          timeouts.forEach(t => clearTimeout(t));
          setTimeout(() => animate(), 1000);
        }
      }, 5000);
    };

    animate();

    return () => {
      isMounted = false;
      intervals.forEach(i => clearInterval(i));
      timeouts.forEach(t => clearTimeout(t));
      if (loopTimeout) clearTimeout(loopTimeout);
    };
  }, []);

  // Avatar positions - 4 avatars clustered around cards, all touching cards
  const avatars = [
    { src: '/avatar1.png', top: '14%', left: '8%' },
    { src: '/avatar2.png', top: '15%', right: '10%' },
    { src: '/avatar3.png', top: '70%', left: '8%' },
    { src: '/avatar4.png', top: '68%', right: '12%' },
  ];

  return (
    <section className="relative bg-white overflow-hidden text-gray-800 mb-0 min-h-[60vh] md:min-h-[80vh]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 h-full flex items-center pt-12 md:pt-20 pb-6 md:pb-16">
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-20 items-center">
          {/* Left Side - Text Content */}
          <div className="text-center lg:text-left space-y-3 md:space-y-6 lg:pr-8">
            {/* Main Heading */}
            <div className="space-y-2 md:space-y-4">
              <h1 className="text-5xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-gray-900 leading-tight">
                KEEP YOUR<br />
                CHAIRS FILLED.<br />
                <span className="bg-gradient-to-r from-green-500 to-green-600 bg-clip-text text-transparent">
                GROW ON AUTOPILOT.
                </span>
              </h1>
              
              <p className="text-base md:text-xl lg:text-2xl text-gray-600 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Stop losing clients between treatments. AI-powered marketing that fills your schedule, drives rebookings, and grows your med spa—automatically.
              </p>
            </div>

            {/* Demo Video CTA */}
            <div className="space-y-1.5 md:space-y-3 flex flex-col items-center lg:items-start">
              <div
                onClick={() => setModalOpen(true)}
                className="inline-flex items-center justify-center gap-2 md:gap-3 px-4 md:px-8 py-2.5 md:py-4 rounded-full border-2 border-gray-200 bg-white shadow-sm hover:shadow-md hover:bg-gray-50 transition-all duration-200 cursor-pointer"
              >
                <span className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#c4ffa0]">
                  <Play className="w-5 h-5 md:w-6 md:h-6 text-black ml-0.5" fill="currentColor" />
                </span>
                <span className="text-sm md:text-lg font-semibold text-gray-900">
                  Watch Demo Video
                </span>
              </div>

              <div className="text-sm md:text-base text-gray-500">
                ✨ No credit card required • Cancel anytime • 7-day free trial
              </div>
            </div>
          </div>

          {/* Right Side - Results Cards with Floating Avatars - Hidden on Mobile */}
          <div className="hidden lg:block relative w-full h-[550px] lg:h-[650px] flex items-start justify-center pt-16 lg:pt-20">
            {/* Results Cards - Spaced for Readability */}
            <div className="relative z-10 w-full max-w-4xl h-full flex items-start justify-center pt-8 transform translate-x-[5%] -translate-y-[20%]">
              {/* Card 1 - Total Audience (Front, Smallest) */}
              <div className="absolute top-12 right-0 w-[320px] lg:w-[340px] bg-white rounded-2xl p-6 lg:p-7 border border-indigo-100/50 shadow-2xl transform -rotate-12 hover:shadow-3xl transition-shadow duration-300 backdrop-blur-sm bg-gradient-to-br from-indigo-50/80 to-purple-50/80 z-30">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-base font-semibold text-gray-800">Total Clients</span>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="text-4xl lg:text-5xl font-bold text-indigo-700 tracking-tight">
                    {totalAudience.toLocaleString()}
                  </div>
                  <div className="flex items-center space-x-2 bg-green-50 rounded-lg px-4 py-2 w-fit">
                    <ArrowUpRight className="w-5 h-5 text-green-600" />
                    <div className="text-base font-bold text-green-700">{growthRate}%</div>
                    <span className="text-sm text-gray-600">growth</span>
                  </div>
                  {/* Mini bar chart */}
                  <div className="flex items-end space-x-2 h-14 mt-4">
                    {barHeights.map((height, i) => (
                      <div key={i} className="flex-1 bg-indigo-100 rounded-t transition-all duration-500" style={{ height: `${Math.max(height, 5)}%` }}>
                        <div className="w-full h-full bg-gradient-to-t from-indigo-400 to-indigo-500 rounded-t"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Card 2 - Engagement Rate (Middle, Medium Size) */}
              <div className="absolute top-32 left-1/2 w-[380px] lg:w-[400px] bg-white rounded-2xl p-6 lg:p-7 border border-green-100/50 shadow-2xl transform -translate-x-1/2 rotate-12 z-20 hover:shadow-3xl transition-shadow duration-300 backdrop-blur-sm bg-gradient-to-br from-green-50/80 to-emerald-50/80">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center shadow-md">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-base font-semibold text-gray-800">Rebooking Rate</span>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="text-4xl lg:text-5xl font-bold text-green-700 tracking-tight">
                    {engagementRate.toFixed(0)}%
                  </div>
                  <div className="h-4 bg-green-100 rounded-full overflow-hidden shadow-inner">
                    <div 
                      className="h-full bg-gradient-to-r from-green-400 to-green-600 rounded-full transition-all duration-500 shadow-sm"
                      style={{ width: `${engagementRate}%` }}
                    ></div>
                  </div>
                  {/* Mini line chart */}
                  <div className="h-12 mt-4 relative">
                    <svg className="w-full h-full" viewBox="0 0 100 40" preserveAspectRatio="none">
                      {chartData.length > 0 && (
                        <polyline
                          fill="none"
                          stroke="url(#greenGradient)"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          points={chartData.map((d, i) => `${(i / Math.max(chartData.length - 1, 1)) * 100},${40 - (d / 100) * 40}`).join(' ')}
                          className="transition-all duration-300"
                        />
                      )}
                      <defs>
                        <linearGradient id="greenGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#4ade80" />
                          <stop offset="100%" stopColor="#22c55e" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Card 3 - Customers (Back, Largest) */}
              <div className="absolute top-56 left-0 w-[450px] lg:w-[480px] bg-white rounded-2xl p-6 lg:p-7 border border-purple-100/50 shadow-2xl transform rotate-12 z-10 hover:shadow-3xl transition-shadow duration-300 backdrop-blur-sm bg-gradient-to-br from-purple-50/80 to-pink-50/80">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
                      <TrendingUp className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-base font-semibold text-gray-800">New Appointments</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="text-4xl lg:text-5xl font-bold text-purple-700 tracking-tight">
                    {Math.round(conversions)}
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                    <span>This month</span>
                  </div>
                  {/* Mini sparkline */}
                  <div className="h-10 mt-3 relative">
                    <svg className="w-full h-full" viewBox="0 0 100 30" preserveAspectRatio="none">
                      <polyline
                        fill="none"
                        stroke="url(#purpleGradient)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        points="0,25 15,20 30,15 45,18 60,12 75,10 90,8 100,5"
                        className="transition-all duration-300"
                      />
                      <defs>
                        <linearGradient id="purpleGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#a855f7" />
                          <stop offset="100%" stopColor="#ec4899" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Avatars - Clustered around cards, no animation */}
            {avatars.map((avatar, index) => {
              // Determine transform based on position
              let baseTransform = 'translate(-50%, -50%)';
              if (avatar.top !== undefined && avatar.left !== undefined) {
                baseTransform = 'translate(-50%, -50%)'; // left side (top or bottom)
              } else if (avatar.top !== undefined && avatar.right !== undefined) {
                baseTransform = 'translate(50%, -50%)'; // right side (top or bottom)
              }
              
              return (
                <div
                  key={index}
                  className="absolute z-40 w-16 h-16 lg:w-20 lg:h-20 rounded-full border-4 border-white shadow-xl hover:scale-110 transition-transform duration-300"
                  style={{
                    transform: baseTransform,
                    top: avatar.top,
                    left: avatar.left,
                    right: avatar.right,
                    bottom: avatar.bottom,
                  }}
                >
                  <img
                    src={avatar.src}
                    alt={`Avatar ${index + 1}`}
                    className="w-full h-full rounded-full object-cover"
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <KinosLeadModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        email=""
      />
    </section>
  );
};

export default MedSpaHeroSection;

